if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            "slideChanged",
            debounce(this.onSlideChanged.bind(this), 500)
          );
          this.elements.viewer.addEventListener("click", (event) => {
            const mediaItem = event.target.closest(
              ".product__media-item[data-media-id]"
            );
            if (mediaItem) this.selectVariantForMediaElement(mediaItem);
          });
        }
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.enableSliderLooping = true;
        this.elements.thumbnails.addEventListener("click", (event) => {
          const thumb = event.target.closest(
            ".thumbnail-list__item[data-target]"
          );
          if (thumb) {
            this.setActiveMedia(thumb.dataset.target, false);
            this.selectVariantForMediaElement(thumb);
          }
        });
        this.elements.thumbnails
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) =>
            button.addEventListener(
              "click",
              this.onThumbnailArrowClick.bind(this),
              { capture: true }
            )
          );
        this.updateThumbnailSlider();
        if (
          this.dataset.groupVariants === "true" &&
          this.dataset.showAllVariantsSorted !== "true"
        ) {
          this.sortThumbnailsByVariantAndPosition();
          this.sortViewerSlidesByVariantAndPosition();
        }
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      getVariantGroupSortOrder(items) {
        const groupNames = [
          ...new Set(
            items.map((el) => el.dataset?.variantGrouping ?? el.getAttribute?.("data-variant-grouping") ?? "")
          ),
        ].filter(Boolean);
        groupNames.sort((a, b) => {
          const numA = parseInt(a, 10);
          const numB = parseInt(b, 10);
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
          if (!Number.isNaN(numA)) return -1;
          if (!Number.isNaN(numB)) return 1;
          return String(a).localeCompare(String(b), undefined, { numeric: true });
        });
        const orderMap = new Map(groupNames.map((name, i) => [name, i]));
        return (value) => {
          const v = value ?? "";
          return orderMap.has(v) ? orderMap.get(v) : orderMap.size;
        };
      }

      sortThumbnailsByVariantAndPosition() {
        const thumbnailList =
          this.elements?.thumbnails?.querySelector(".thumbnail-list");
        if (!thumbnailList) return;
        const items = Array.from(
          thumbnailList.querySelectorAll(
            ".thumbnail-list__item[data-media-position]"
          )
        );
        if (items.length <= 1) return;

        const groupOrder = this.getVariantGroupSortOrder(items);
        const groups = new Map();
        for (const item of items) {
          const group = item.dataset.variantGrouping ?? "";
          if (!groups.has(group)) groups.set(group, []);
          groups.get(group).push(item);
        }
        const uniqueNames = [...new Set(items.map((i) => i.dataset.variantGrouping ?? ""))];
        const sortedGroupNames = uniqueNames.filter(Boolean).sort((a, b) => groupOrder(a) - groupOrder(b));
        const orderToApply = sortedGroupNames.length < uniqueNames.length ? [...sortedGroupNames, ""] : sortedGroupNames;
        for (const groupName of orderToApply) {
          for (const item of groups.get(groupName) ?? []) {
            thumbnailList.appendChild(item);
          }
        }
      }

      sortViewerSlidesByVariantAndPosition() {
        const viewer = this.elements?.viewer;
        if (!viewer) return;
        const slideList =
          viewer.querySelector(
            ".product__media-list, .product__media-list--single, [role='list']"
          ) || viewer.querySelector("ul");
        if (!slideList) return;
        const slides = Array.from(
          slideList.querySelectorAll(".product__media-item[data-media-id]")
        );
        if (slides.length <= 1) return;
        const thumbnailList =
          this.elements?.thumbnails?.querySelector(".thumbnail-list");
        if (!thumbnailList) return;
        const thumbOrder = Array.from(
          thumbnailList.querySelectorAll(
            ".thumbnail-list__item[data-target][data-media-position]"
          )
        ).map((el) => el.dataset.target);
        if (thumbOrder.length === 0) return;
        const byId = new Map(slides.map((el) => [el.dataset.mediaId, el]));
        for (const target of thumbOrder) {
          const slide = byId.get(target);
          if (slide) slideList.appendChild(slide);
        }
      }

      onSlideChanged(event) {
        const currentSlide = event.detail?.currentElement;
        if (currentSlide) this.selectVariantForMediaElement(currentSlide);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentSlide?.dataset?.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      selectVariantForMediaElement(element) {
        const productInfo = this.closest("product-info");
        if (!productInfo?.selectVariantById) return;

        const currentVariantId = productInfo.getActiveVariantId?.();
        if (
          currentVariantId &&
          productInfo.elementMatchesVariant?.(element, currentVariantId)
        )
          return;

        const variantId = element?.dataset?.variantId;
        if (!variantId) return;
        productInfo.selectVariantById(variantId);
      }

      setActiveMedia(mediaId, prepend) {
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            ".product__media-item[data-media-id]"
          )
        );
        const isVisible = (element) => !element.classList.contains("hide-image");
        const firstVisibleMedia =
          mediaItems.find((item) => isVisible(item)) || mediaItems[0];
        let activeMedia =
          this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${mediaId}"]`
          ) || firstVisibleMedia;
        if (activeMedia && !isVisible(activeMedia)) {
          activeMedia = firstVisibleMedia;
        }

        if (!activeMedia) {
          return;
        }
        const resolvedMediaId = activeMedia.dataset.mediaId;
        this.elements.viewer
          .querySelectorAll(".product__media-item[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        const shouldPrepend =
          prepend && activeMedia.parentElement.firstChild !== activeMedia;

        if (shouldPrepend) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${resolvedMediaId}"]`
            );
            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.resetPages)
            this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const scrollContainer = activeMedia.parentElement;
          if (
            scrollContainer &&
            (!this.mql.matches || this.elements.thumbnails)
          ) {
            scrollContainer.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const visibleThumbnails = Array.from(
          this.elements.thumbnails.querySelectorAll(
            ".thumbnail-list__item[data-target]"
          )
        ).filter((item) => !item.classList.contains("hide-image"));
        let activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${resolvedMediaId}"]`
        );
        if (
          !activeThumbnail ||
          activeThumbnail.classList.contains("hide-image")
        ) {
          activeThumbnail = visibleThumbnails[0];
        }
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
        this.updateThumbnailSlider();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      updateThumbnailSlider() {
        if (!this.elements.thumbnails) return;
        const maxVisible =
          Number(this.elements.thumbnails.dataset.maxVisible) || 6;
        const thumbnailList =
          this.elements.thumbnails.querySelector(".thumbnail-list");
        if (!thumbnailList) return;

        const visibleItems = Array.from(
          thumbnailList.querySelectorAll(".thumbnail-list__item")
        ).filter((item) => !item.classList.contains("hide-image"));
        const shouldShowArrows = visibleItems.length > maxVisible;
        this.elements.thumbnails
          .querySelectorAll(".slider-button")
          .forEach((button) =>
            button.toggleAttribute("hidden", !shouldShowArrows)
          );
        this.elements.thumbnails.classList.toggle(
          "thumbnail-slider--no-slide",
          !shouldShowArrows
        );

        this.elements.thumbnails
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => {
            if (!shouldShowArrows) {
              button.removeAttribute("disabled");
              return;
            }
            button.removeAttribute("disabled");
          });
      }

      onThumbnailArrowClick(event) {
        if (!this.elements.thumbnails) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();

        const thumbnailList =
          this.elements.thumbnails.querySelector(".thumbnail-list");
        if (!thumbnailList) return;

        const visibleItems = Array.from(
          thumbnailList.querySelectorAll(".thumbnail-list__item")
        ).filter((item) => !item.classList.contains("hide-image"));
        if (visibleItems.length < 2) return;

        const activeButton = thumbnailList.querySelector(
          'button[aria-current="true"]'
        );
        const activeItem = activeButton?.closest(".thumbnail-list__item");
        const activeIndex = activeItem ? visibleItems.indexOf(activeItem) : 0;
        if (activeIndex === -1) return;

        const direction = event.currentTarget.name === "next" ? 1 : -1;
        const nextIndex =
          (activeIndex + direction + visibleItems.length) % visibleItems.length;

        const nextTarget = visibleItems[nextIndex].dataset.target;
        this.setActiveMedia(nextTarget, false);
        const mediaItem = this.elements.viewer?.querySelector(
          `.product__media-item[data-media-id="${nextTarget}"]`
        );
        if (mediaItem) this.selectVariantForMediaElement(mediaItem);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
